import type { LangCode } from "@/lib/types";
import { VALID_LANGS } from "./locale";
import type { LocaleCode, SeoBlock, StoreCategorySeo } from "@/lib/api/types";

/**
 * The `<id>` half of Odoo's `<slugified-name>-<id>` slug convention, or null.
 *
 * This is the only part of a shop slug that carries meaning to Odoo — its
 * ir.http._unslug() reads the trailing id and ignores the text entirely. Two
 * slugs with the same id are the same product no matter how the text differs,
 * which is what lets the detail route correct a wrong slug without risking
 * sending anyone to a different product.
 */
export function trailingProductId(slug: string): string | null {
  return slug.match(/-(\d+)$/)?.[1] ?? null;
}

/**
 * True when `requested` should be 301'd to `canonical`.
 *
 * Extracted from the loader so the decision is testable without a router: the
 * loader keeps redirect() and getCurrentLocale(), this keeps the rule. Same
 * split as product-tabs.ts.
 */
export function shouldRedirectToCanonicalSlug(
  requested: string,
  canonical: string | undefined | null,
): boolean {
  if (!canonical || canonical === requested) return false;
  const id = trailingProductId(requested);
  return id !== null && trailingProductId(canonical) === id;
}

/**
 * The bare slug out of an API-supplied `/shop/<slug>` path, or null.
 *
 * Rejects anything that is not a single-segment /shop path. The value arrives
 * in a 404 body from Odoo and ends up in a 301 Location header, so it is
 * treated as untrusted input rather than as a path we can concatenate.
 */
export function canonicalSlugFromPath(path: string | undefined): string | null {
  if (!path) return null;
  const match = path.match(/^\/shop\/([^/?#]+)$/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Odoo's per-locale URLs for a product, as de-localized paths keyed by locale.
 *
 * Returns undefined when Odoo gave us nothing usable, and the caller then emits
 * NO hreflang for the page. That is deliberate and is the whole point of the
 * change: the previous behaviour was to guess a URL for all ten locales by
 * prefix substitution, and every guess resolved 200 and self-canonicalised. No
 * alternate is a missing signal; a wrong alternate is an actively false one.
 */
export function shopHreflangPaths(
  seo: SeoBlock | undefined,
  /**
   * This product's own de-localized path, e.g. `/shop/revive-artemia-115`.
   *
   * Used for the locales Odoo does not name. Omit it to keep the old
   * behaviour of emitting alternates only for the locales Odoo listed.
   */
  selfPath?: string,
): Partial<Record<LangCode, string>> | undefined {
  const hreflang = seo?.hreflang;
  if (!hreflang) return undefined;

  const paths: Partial<Record<LangCode, string>> = {};
  for (const [locale, href] of Object.entries(hreflang) as [LocaleCode, string][]) {
    if (!href || !VALID_LANGS.includes(locale as LangCode)) continue;
    const path = deLocalizedPath(href);
    if (path) paths[locale as LangCode] = path;
  }
  if (Object.keys(paths).length === 0) return undefined;

  // VARS-085. Odoo names only the languages installed in it — seven today, so
  // `es`, `zh` and `fa` got no alternate at all while the sitemap submitted all
  // ten. A cluster where three members are submitted for indexing but never
  // referenced by the others is non-reciprocal, and Google discards the whole
  // set rather than the missing edge.
  //
  // The site does serve those three: this store's listing endpoint answers for
  // every locale, falling back to the default language for the ones Odoo has no
  // translation for — verified against production on 2026-09-13, where the
  // `es`, `zh` and `fa` listings return exactly the `en` slugs. So the honest
  // alternate for a locale Odoo does not name is this product's own path, which
  // is the URL that locale actually serves. This is NOT the prefix-substitution
  // guessing the override mechanism was built to remove: that invented a path
  // per locale, this reuses the one path every unlisted locale really resolves.
  //
  // If a language is later installed in Odoo it appears in `hreflang` with its
  // own translated slug and the loop above wins, because the fill only ever
  // touches locales the API left out.
  if (selfPath) {
    for (const lang of VALID_LANGS) {
      if (!paths[lang]) paths[lang] = selfPath;
    }
  }
  return paths;
}

/**
 * The de-localized, still-encoded path of an absolute or relative Odoo URL.
 *
 * __root.tsx builds an alternate as `SITE_URL + /<lang> + path`, so what it
 * needs here is the path with any locale prefix already removed — mirroring
 * what server.ts's deLocalizeRequest does to the incoming request. Anything
 * that is not a /shop path is rejected rather than trusted: this value ends up
 * in a public hreflang, and Odoo is upstream of us.
 */
function deLocalizedPath(href: string): string | null {
  let pathname: string;
  try {
    pathname = href.startsWith("/") ? href : new URL(href).pathname;
  } catch {
    return null;
  }
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length > 0 && VALID_LANGS.includes(parts[0].toLowerCase() as LangCode)) {
    parts.shift();
  }
  const path = "/" + parts.join("/");
  return path.startsWith("/shop/") ? path : null;
}

/**
 * Per-locale paths for a store category, from the listing's `meta.categories`.
 *
 * Same shape and same rules as shopHreflangPaths() above — Odoo names only the
 * languages installed in it, and the locales it leaves out are filled with the
 * path they actually serve (VARS-124, the category twin of VARS-085). Returns
 * undefined when the listing carried no category metadata at all, which is the
 * case against an Odoo older than 19.0.1.17.0: the page then emits no
 * alternates, rather than the locale-prefix guesses it used to.
 */
export function categoryHreflangPaths(
  categories: StoreCategorySeo[] | undefined,
  slug: string,
  selfPath?: string,
): Partial<Record<LangCode, string>> | undefined {
  // Matched on the trailing id, not the slug text: the caller's slug is in the
  // locale being rendered, and the entry it belongs to is the one for the same
  // category in any locale.
  const id = trailingProductId(slug);
  const entry = categories?.find(
    (c) => c.slug === slug || (id !== null && trailingProductId(c.slug) === id),
  );
  if (!entry?.hreflang) return undefined;

  const paths: Partial<Record<LangCode, string>> = {};
  for (const [locale, href] of Object.entries(entry.hreflang) as [LocaleCode, string][]) {
    if (!href || !VALID_LANGS.includes(locale as LangCode)) continue;
    const path = deLocalizedCategoryPath(href);
    if (path) paths[locale as LangCode] = path;
  }
  if (Object.keys(paths).length === 0) return undefined;

  if (selfPath) {
    for (const lang of VALID_LANGS) {
      if (!paths[lang]) paths[lang] = selfPath;
    }
  }
  return paths;
}

/**
 * The slug this locale actually serves for the id in `slug`, or null.
 *
 * Used for both products and categories, whose slugs follow the same
 * `<slugified-name>-<id>` convention and are both resolved by Odoo on the id
 * alone. `knownSlugs` is whatever the caller already loaded in the current
 * locale — the product slugs of a listing, or the category slug on each of
 * them — which is the point: those values are in the locale being rendered,
 * which is what a redirect target has to be, and they exist against every
 * version of the API, so the correction does not wait on an Odoo upgrade.
 */
export function canonicalSlugForId(
  knownSlugs: (string | undefined)[],
  slug: string,
): string | null {
  const id = trailingProductId(slug);
  if (id === null) return null;
  return knownSlugs.find((s): s is string => !!s && trailingProductId(s) === id) ?? null;
}

function deLocalizedCategoryPath(href: string): string | null {
  let pathname: string;
  try {
    pathname = href.startsWith("/") ? href : new URL(href).pathname;
  } catch {
    return null;
  }
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length > 0 && VALID_LANGS.includes(parts[0].toLowerCase() as LangCode)) {
    parts.shift();
  }
  const path = "/" + parts.join("/");
  return path.startsWith("/shop/category/") ? path : null;
}
